import { Button, Modal, addToast } from '@octano/global-ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  createGatewayPayment,
  createIcebergPayment,
  getIcebergPaymentStatus,
} from '../requests';

interface PaymentModalProps {
  isOpen: boolean;
  certificateId: string | null;
  certificateName: string;
  paymentAmount: string;
  icebergEnabled: boolean;
  onClose: () => void;
  onPaymentCompleted: () => void;
}

type ModalStep =
  | 'confirm'
  | 'iceberg-creating'
  | 'iceberg-created'
  | 'verifying';

export default function PaymentModal({
  isOpen,
  certificateId,
  certificateName,
  paymentAmount,
  icebergEnabled,
  onClose,
  onPaymentCompleted,
}: PaymentModalProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<ModalStep>('confirm');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    setStep('confirm');
    setLoading(false);
    onClose();
  };

  const handleGatewayPayment = async () => {
    if (!certificateId) {
      return;
    }
    setLoading(true);

    const { data, error } = await createGatewayPayment(certificateId);
    setLoading(false);

    if (error || !data?.url) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('reports.payment.errorCreatingPayment'),
      });
      return;
    }

    // Redirect to gateway
    window.location.href = data.url;
  };

  const handleIcebergPayment = async () => {
    if (!certificateId) {
      return;
    }
    setLoading(true);
    setStep('iceberg-creating');

    const { error } = await createIcebergPayment(certificateId);
    setLoading(false);

    if (error) {
      setStep('confirm');
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('reports.payment.errorCreatingIcebergDebt'),
      });
      return;
    }

    setStep('iceberg-created');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleVerifyIceberg = async () => {
    if (!certificateId) {
      return;
    }
    setLoading(true);

    const { data, error } = await getIcebergPaymentStatus(certificateId);
    setLoading(false);

    if (error) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('reports.payment.errorVerifyingPayment'),
      });
      return;
    }

    if (data?.status === 'success') {
      onPaymentCompleted();
      handleClose();
      addToast({
        icon: 'success',
        color: 'success',
        text: t('reports.payment.paymentConfirmed'),
      });
    } else if (data?.status === 'failed') {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('reports.payment.paymentFailed'),
      });
    } else {
      addToast({
        icon: 'information',
        color: 'warning',
        text: t('reports.payment.paymentStillProcessing'),
      });
    }
  };

  const renderConfirmStep = () => (
    <>
      <div className="text-center mb-4">
        <h5 className="mb-2">{certificateName}</h5>
        <p className="mb-1">{t('reports.payment.amountLabel')}</p>
        <h3 className="text-primary">${paymentAmount}</h3>
      </div>
      <div className="d-flex justify-content-center" style={{ gap: 12 }}>
        <Button
          text={t('reports.payment.cancel')}
          outlined
          onClick={handleClose}
          size="sm"
        />
        <Button
          text={t('reports.payment.goToPay')}
          onClick={icebergEnabled ? handleIcebergPayment : handleGatewayPayment}
          loading={loading}
          size="sm"
        />
      </div>
    </>
  );

  const renderIcebergCreating = () => (
    <div className="text-center">
      <p>{t('reports.payment.creatingDebt')}</p>
    </div>
  );

  const renderIcebergCreated = () => (
    <>
      <div className="text-center mb-4">
        <div className="mb-3">
          <span style={{ fontSize: 48, color: '#28a745' }}>&#10003;</span>
        </div>
        <p className="mb-2">{t('reports.payment.icebergDebtCreated')}</p>
        <p className="text-muted">{t('reports.payment.icebergGoToPay')}</p>
      </div>
      <div className="d-flex justify-content-center" style={{ gap: 12 }}>
        <Button
          text={t('reports.payment.close')}
          outlined
          onClick={handleClose}
          size="sm"
        />
      </div>
    </>
  );

  return (
    <Modal isOpen={isOpen} toggle={handleClose}>
      <div className="p-4">
        <h4 className="text-center mb-4">{t('reports.payment.title')}</h4>
        {step === 'confirm' && renderConfirmStep()}
        {step === 'iceberg-creating' && renderIcebergCreating()}
        {step === 'iceberg-created' && renderIcebergCreated()}
      </div>
    </Modal>
  );
}
